// ─── 대시보드 섹션 ────────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, AlertTriangle, Zap, Calendar, CheckCircle } from 'lucide-react';
import { STATUS_META, STATUS_COLORS } from '../../constants/adminConstants';
import { fmtMs, fmtTime, todayStr, dateOffset } from '../../utils/formatters';
import { rowTimeMs } from '../../utils/dataHelpers';
import KpiCard       from './KpiCard';
import TrendBadge    from './TrendBadge';
import TopUsersTable from './TopUsersTable';
import StatusBadge   from '../shared/StatusBadge';
import ScoreBar      from '../shared/ScoreBar';
import DonutChart    from '../charts/DonutChart';
import TrendChart    from '../charts/TrendChart';
import HourlyBars    from '../charts/HourlyBars';
import DetailModal   from '../modals/DetailModal';

// ── 통계 계산 ─────────────────────────────────────────────────────────────────
function calcStats(rows, td, ydStr) {
  const todayCnt   = rows.filter((r) => r.chat_time?.slice(0, 10) === td).length;
  const ydCnt      = rows.filter((r) => r.chat_time?.slice(0, 10) === ydStr).length;
  const successCnt = rows.filter((r) => r.answer_status === 'success').length;
  const msRows     = rows.filter((r) => r.latency_ms != null);

  const avgMs  = msRows.length ? Math.round(msRows.reduce((a, r) => a + r.latency_ms, 0) / msRows.length) : null;
  const sorted = [...msRows].sort((a, b) => a.latency_ms - b.latency_ms);
  const p90Ms  = sorted.length >= 5 ? sorted[Math.floor(sorted.length * 0.9)]?.latency_ms : null;
  const okRate = rows.length ? ((successCnt / rows.length) * 100).toFixed(1) : '0';

  return { todayCnt, ydCnt, successCnt, avgMs, p90Ms, okRate, total: rows.length };
}

/**
 * 대시보드 메인 섹션 – KPI 카드, 7일 추이, 상태 분포, 시간별 차트, 품질 분포, 긴급 항목, 상위 이용자, 최근 질의를 포함합니다.
 * @param {{ rows: Array }} props
 */
export default function DashboardSection({ rows }) {
  const [detail, setDetail] = useState(null);

  const td    = todayStr();
  const ydStr = dateOffset(-1);

  const stats = useMemo(() => calcStats(rows, td, ydStr), [rows, td, ydStr]);

  const last7 = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const dayRows = rows.filter((r) => r.chat_time?.slice(0, 10) === key);
        return {
          label: `${d.getMonth() + 1}/${d.getDate()}`,
          n:     dayRows.length,
          error: dayRows.filter((r) => r.answer_status === 'error' || r.answer_status === 'timeout').length,
        };
      }),
    [rows]
  );

  const statusSegs = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.answer_status] = (m[r.answer_status] || 0) + 1; });
    return Object.entries(m).map(([k, v]) => ({
      label: STATUS_META[k]?.label || k.toUpperCase(),
      value: v,
      color: STATUS_COLORS[k] || '#94a3b8',
    }));
  }, [rows]);

  const urgent = useMemo(
    () =>
      rows
        .filter((r) => (r.eval_score != null && r.eval_score < 70) || r.answer_status === 'error' || r.answer_status === 'timeout')
        .sort((a, b) => (a.eval_score ?? 0) - (b.eval_score ?? 0))
        .slice(0, 5),
    [rows]
  );

  const recentRows = useMemo(
    () =>
      [...rows]
        .sort((a, b) => {
          const diff = rowTimeMs(b) - rowTimeMs(a);
          if (diff !== 0) return diff;
          return String(b.turn_id).localeCompare(String(a.turn_id), undefined, { numeric: true });
        })
        .slice(0, 6),
    [rows]
  );

  return (
    <div className="space-y-5">
      {/* ── Row 1: KPI 카드 5개 ── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard icon={MessageSquare} iconBg="bg-blue-50"    iconColor="text-sb-primary"
          value={stats.total.toLocaleString()} label="전체 질의 수" sub={`성공률 ${stats.okRate}%`} />
        <KpiCard icon={Calendar}     iconBg="bg-indigo-50"  iconColor="text-indigo-600"
          value={stats.todayCnt} label="오늘 질의" sub={`어제 ${stats.ydCnt}건`}
          badge={<TrendBadge curr={stats.todayCnt} prev={stats.ydCnt} />} />
        <KpiCard icon={CheckCircle}  iconBg="bg-emerald-50" iconColor="text-emerald-600"
          value={`${stats.okRate}%`} label="성공률" sub={`성공 ${stats.successCnt} / 전체 ${stats.total}건`} />
        <KpiCard icon={Zap}          iconBg="bg-amber-50"   iconColor="text-amber-600"
          value={stats.avgMs != null ? fmtMs(stats.avgMs) : '—'} label="평균 응답시간"
          sub={stats.p90Ms != null ? `P90 ${fmtMs(stats.p90Ms)}` : '측정된 건만'} />
      </div>

      {/* ── Row 2: 7일 추이 + 도넛 ── */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="flex flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-3 flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-800">최근 7일 질문 추이</p>
              <p className="text-sm text-gray-500">일별 전체 건수 및 오류 발생 추이</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded bg-blue-500" />전체</span>
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded bg-red-400" />오류</span>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <TrendChart data={last7} />
          </div>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <p className="mb-1 text-base font-semibold text-gray-800">응답 상태 분포</p>
          <p className="mb-4 text-sm text-gray-500">전체 기간 기준</p>
          <DonutChart segs={statusSegs} size={134} />
        </div>
      </div>

      {/* ── Row 3: 시간별 ── */}
      <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <p className="mb-1 text-base font-semibold text-gray-800">오늘 시간별 질의</p>
        <p className="mb-3 text-sm text-gray-500">당일 시간대별 질의 건수 분포</p>
        <HourlyBars rows={rows} />
      </div>

      {/* ── Row 4: 즉각 조치 + 상위 이용자 ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 즉각 조치 필요 */}
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <p className="text-base font-semibold text-gray-800">즉각 조치 필요</p>
            <p className="text-sm text-gray-500">저점수 · 오류 · 타임아웃 상위 5건</p>
          </div>
          {urgent.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">문제 항목 없음 ✓</p>
          ) : (
            <ul>
              {urgent.map((item) => (
                <li
                  key={item.turn_id}
                  className="flex cursor-pointer items-center gap-3 border-b border-gray-50 px-5 py-3 hover:bg-gray-50"
                  onClick={() => setDetail(item)}
                >
                  <StatusBadge status={item.answer_status} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base text-gray-800">{item.question || '(빈 질문)'}</p>
                    <p className="text-sm text-gray-400">{item.user_name} · {item.company_name || item.company_code}</p>
                  </div>
                  <ScoreBar score={item.eval_score} compact />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 상위 이용자 TOP 8 */}
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <div>
              <p className="text-base font-semibold text-gray-800">상위 이용자 TOP 8</p>
              <p className="text-sm text-gray-500">전체 기간 질의 건수 기준</p>
            </div>
            <span className="text-xs font-medium text-gray-400">성공률</span>
          </div>
          <div className="px-5 py-4">
            <TopUsersTable rows={rows} />
          </div>
        </div>
      </div>

      {/* ── Row 5: 최근 질의 ── */}
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-base font-semibold text-gray-800">최근 질의</p>
            <p className="text-sm text-gray-500">일시 기준 최신 6건 · 클릭 시 상세</p>
          </div>
          <Link to="/ai/admin/inquiries" className="text-sm font-semibold text-sb-primary hover:text-blue-700">
            전체 보기 →
          </Link>
        </div>
        <ul className="divide-y divide-gray-50">
          {recentRows.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-gray-400">표시할 질의가 없습니다</li>
          ) : (
            recentRows.map((item) => (
              <li
                key={item.turn_id}
                className="flex cursor-pointer items-center gap-3 px-5 py-3 hover:bg-gray-50"
                onClick={() => setDetail(item)}
              >
                <StatusBadge status={item.answer_status} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base text-gray-800">{item.question || '(빈 질문)'}</p>
                  <p className="text-sm text-gray-400">{item.user_name} · {item.company_name || item.company_code}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-mono text-base text-gray-500">{fmtTime(item.chat_time)}</p>
                  {item.latency_ms != null && (
                    <p className="font-mono text-sm text-gray-400">{fmtMs(item.latency_ms)}</p>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
